import clsx from 'clsx';
import type { ReactNode } from 'react';

import styles from './Tabs.module.scss';

export type Tab<T extends string> = {
  value: T;
  label: ReactNode;
  /** Sits before the label. Hidden below 940px, where the row has no space for it. */
  icon?: ReactNode;
  /** Kept in the list but not rendered — for a tab a permission or a feature flag switches off. */
  hidden?: boolean;
};

type TabsProps<T extends string> = {
  tabs: readonly Tab<T>[];
  activeTab: T;
  onChange: (value: T) => void;
  className?: string;
};

/**
 * Tabs move between sections that each own their content — `Summary · Guest list · Menu`,
 * `Share · Permissions · Activity`. Full width, the active one underlined.
 *
 * The other shape is `SegmentedControl`, and the two are told apart by where the control
 * sits, not by how it looks: a control standing in a toolbar or a card header **above**
 * content it filters or re-renders — a calendar, a table, a list, a form — is a segmented
 * control, and so is a step switcher inside a wizard modal. Tabs are for the case where
 * each option leads somewhere else.
 *
 * These are the only two. A third shape is a defect, not a variant.
 */
const Tabs = <T extends string>({ tabs, activeTab, onChange, className }: TabsProps<T>) => (
  <div className={clsx(styles.tabs, className)} role="tablist">
    {tabs
      .filter((tab) => !tab.hidden)
      .map(({ value, label, icon }) => (
        <button
          key={value}
          type="button"
          role="tab"
          aria-selected={activeTab === value}
          className={clsx(styles.tab, activeTab === value && styles.tabActive)}
          onClick={() => onChange(value)}
        >
          {icon && <span className={styles.icon}>{icon}</span>}
          {label}
        </button>
      ))}
  </div>
);

export default Tabs;
